import Button from '@blocks/Button'
import Clipboard from '@blocks/Clipboard'
import EmphasizedContent from '@blocks/EmphasizedContent'
import Grid from '@blocks/Grid'
import MainContainer from '@blocks/MainContainer'
import NavigationLow from '@blocks/NavigationLow'
import ClubController from '@club/customer/api/ClubController'
import SecretController from '@club/customer/api/SecretController'
import { useConfigContext } from '@contexts/config'
import { confirmation, notify, route } from '@helpers'
import useAuth from '@hooks/useAuth'
import useTitle from '@hooks/useTitle'
import { ReactComponent as EditIcon } from '@icons/edit.svg'
import { colors } from '@styles/vars'
import useMainTemplate from '@templates/Main/hooks/useMainTemplate'
import React from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import styled from 'styled-components'
import QuickLinks from './components/QuickLinks'

export default function Owner() {
    const { user, ...auth } = useAuth<Resources.ClubOwner>()
    const config = useConfigContext()

    useTitle('Group: ' + user.club.name)
    useMainTemplate({ category: "club" })

    const recreate = async () => {
        const secret = await SecretController.store(user.club.id)
        auth.set('club.secret', secret)
        notify("Created a new link for the group.")
    }

    const destroy = async () => {
        const confirmed = await confirmation('Are you sure you want to delete the group? You won\'t be able to restore it.')
        if (!confirmed) return

        await ClubController.destroy(user.club.id)

        unstable_batchedUpdates(() => {
            auth.set('club', null)
            auth.set('membership', null)    
        })
        
        notify("The group was deleted.")
    }

    return (
        <MainContainer heading={user.club.name}>
            <Grid>
                <Grid>
                    <Grid gap="10px">
                        <Heading>
                            <h1 className="h3">{user.club.name}</h1>
                            <Button type="tertiary" width="auto" icon={<EditIcon />} to={route("club:owner:edit")}>Edit</Button>
                        </Heading>
                        <p style={{ maxWidth: "450px" }}>You're the head of the group. Invite people to become a member of your group and monitor their activities.</p>
                    </Grid>
                    <EmphasizedContent>
                        <Grid columns="2">
                            <Grid gap="15px">
                                <h4>Use the link to invite members:</h4>
                                <Clipboard value={`${config.url}/app/group/invite/${user.club.secret}`} />
                                <Grid columns="2">
                                    <Grid gap="10px">
                                        <small>Note: If someone unwanted has the access to the link, you can generate a new one.</small>
                                        <Button type="tertiary" onClick={recreate}>Generate</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </EmphasizedContent>
                    <QuickLinks club={user.club} />
                    <Grid gap="10px">
                        <Grid gap="0px">
                            <small>Info</small>
                            <hr />
                        </Grid>
                        <Grid columns="4">
                            <Grid gap="5px">
                                <small>Email</small>
                                <p style={{ color: colors.black }}><b>{user.club.email}</b></p>
                            </Grid>
                            <Grid gap="5px">
                                <small>Delivery</small>
                                <p style={{ color: colors.black }}><b>{user.club.address ? `${user.club.address}, ${user.club.zip}` : "We'll send awards to each member separately."}</b></p>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid gap="10px">
                    <Grid gap="0px">
                        <small>Danger zone</small>
                        <hr />
                    </Grid>
                    <Grid columns="3">
                        <Grid gap="10px">
                            <h4>Delete</h4>
                            <small>This action deletes the whole group without the opportunity to restore it. Please, be careful when you click the button.</small>
                            <Button type="tertiary" width="auto" onClick={destroy}>Delete the group</Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </MainContainer>
    )
}

const Heading = styled.div`
    display: flex;
    align-items: center;

    > *:not(:last-child) {
        margin-right: 15px;
        margin-bottom: 0;
    }
`

const NavigationLowWrapper = styled.div`
    margin: 0 -30px;
`